import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Flex,
  Text,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  useColorModeValue,
  useToast,
  useDisclosure,
  Select,
  Input,
  Stack,
} from '@chakra-ui/react';
import { AddIcon, CloseIcon } from '@chakra-ui/icons';
import { getMovements } from 'services/currentAccount.service';
import CreateMovementModal from './CreateMovementModal';

export default function AccountDetailModal({ isOpen, onClose, account, onUpdate }) {
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    movementType: '',
    status: '',
    page: 1,
    limit: 20,
  });

  const {
    isOpen: isCreateOpen,
    onOpen: onCreateOpen,
    onClose: onCreateClose,
  } = useDisclosure();

  const toast = useToast();
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  useEffect(() => {
    if (isOpen && account) {
      loadMovements();
    }
  }, [isOpen, account, filters]);

  const loadMovements = async () => {
    try {
      setLoading(true);
      const response = await getMovements(account.accountId, filters);
      if (response.status === 200) {
        setMovements(response.payload.movements || []);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los movimientos',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMovementCreated = () => {
    loadMovements();
    onUpdate();
    onCreateClose();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-UY', {
      style: 'currency',
      currency: 'UYU',
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('es-UY', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const getMovementTypeColor = (type) => {
    return type === 'DEBIT' ? 'red' : 'green';
  };

  const getMovementTypeLabel = (type) => {
    return type === 'DEBIT' ? 'Débito' : 'Crédito';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED':
        return 'green';
      case 'PENDING':
        return 'yellow';
      case 'CANCELLED':
        return 'red';
      default:
        return 'gray';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'COMPLETED':
        return 'Completado';
      case 'PENDING':
        return 'Pendiente';
      case 'CANCELLED':
        return 'Cancelado';
      default:
        return status;
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="6xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader pr={12}>
            <Flex
              justify="space-between"
              align={{ base: 'flex-start', md: 'center' }}
              gap={4}
              flexDir={{ base: 'column', md: 'row' }}
              pr={2}
            >
              <Text>Cuenta Corriente - {account?.studentName}</Text>
              <Button
                leftIcon={<AddIcon />}
                colorScheme="blue"
                size="sm"
                onClick={onCreateOpen}
                flexShrink={0}
                mr={2}
              >
                Nuevo Movimiento
              </Button>
            </Flex>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {/* Resumen de cuenta */}
            <Flex gap="15px" mb="20px" flexWrap="wrap">
              <Box
                bg="gray.50"
                p="15px"
                borderRadius="10px"
                flex="1"
                minW="150px"
              >
                <Text fontSize="xs" color="gray.600" mb="5px">
                  Saldo Actual
                </Text>
                <Text
                  fontSize="xl"
                  fontWeight="bold"
                  color={account?.balance > 0 ? 'red.500' : 'green.500'}
                >
                  {formatCurrency(account?.balance)}
                </Text>
              </Box>
              <Box
                bg="red.50"
                p="15px"
                borderRadius="10px"
                flex="1"
                minW="150px"
              >
                <Text fontSize="xs" color="gray.600" mb="5px">
                  Total Débitos
                </Text>
                <Text fontSize="xl" fontWeight="bold" color="red.600">
                  {formatCurrency(account?.totalDebits)}
                </Text>
              </Box>
              <Box
                bg="green.50"
                p="15px"
                borderRadius="10px"
                flex="1"
                minW="150px"
              >
                <Text fontSize="xs" color="gray.600" mb="5px">
                  Total Créditos
                </Text>
                <Text fontSize="xl" fontWeight="bold" color="green.600">
                  {formatCurrency(account?.totalCredits)}
                </Text>
              </Box>
              <Box
                bg="blue.50"
                p="15px"
                borderRadius="10px"
                flex="1"
                minW="150px"
              >
                <Text fontSize="xs" color="gray.600" mb="5px">
                  Movimientos
                </Text>
                <Text fontSize="xl" fontWeight="bold" color="blue.600">
                  {account?.movementsCount}
                </Text>
              </Box>
            </Flex>

            {/* Filtros */}
            <Stack direction="row" spacing={4} mb="20px">
              <Select
                placeholder="Todos los tipos"
                value={filters.movementType}
                onChange={(e) =>
                  setFilters({ ...filters, movementType: e.target.value })
                }
                size="sm"
              >
                <option value="DEBIT">Débitos</option>
                <option value="CREDIT">Créditos</option>
              </Select>
              <Select
                placeholder="Todos los estados"
                value={filters.status}
                onChange={(e) =>
                  setFilters({ ...filters, status: e.target.value })
                }
                size="sm"
              >
                <option value="COMPLETED">Completados</option>
                <option value="PENDING">Pendientes</option>
                <option value="CANCELLED">Cancelados</option>
              </Select>
            </Stack>

            {/* Tabla de movimientos */}
            {!loading && (
              <Box overflowX="auto" maxH="400px" overflowY="auto">
                <Table variant="simple" size="sm">
                  <Thead position="sticky" top={0} bg="white" zIndex={1}>
                    <Tr>
                      <Th borderColor={borderColor}>Fecha</Th>
                      <Th borderColor={borderColor}>Tipo</Th>
                      <Th borderColor={borderColor}>Descripción</Th>
                      <Th borderColor={borderColor} isNumeric>
                        Monto
                      </Th>
                      <Th borderColor={borderColor} isNumeric>
                        Saldo Después
                      </Th>
                      <Th borderColor={borderColor}>Estado</Th>
                      <Th borderColor={borderColor}>Método Pago</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {movements.length === 0 ? (
                      <Tr>
                        <Td colSpan={7} textAlign="center" py="40px">
                          <Text color="gray.500">
                            No hay movimientos registrados
                          </Text>
                        </Td>
                      </Tr>
                    ) : (
                      movements.map((movement) => (
                        <Tr key={movement.id}>
                          <Td borderColor={borderColor}>
                            {formatDate(movement.movementDate)}
                          </Td>
                          <Td borderColor={borderColor}>
                            <Badge
                              colorScheme={getMovementTypeColor(
                                movement.movementType
                              )}
                            >
                              {getMovementTypeLabel(movement.movementType)}
                            </Badge>
                          </Td>
                          <Td borderColor={borderColor}>
                            <Text fontSize="sm">{movement.description}</Text>
                          </Td>
                          <Td borderColor={borderColor} isNumeric>
                            <Text
                              fontWeight="bold"
                              color={
                                movement.movementType === 'DEBIT'
                                  ? 'red.500'
                                  : 'green.500'
                              }
                            >
                              {movement.movementType === 'DEBIT' ? '+' : '-'}
                              {formatCurrency(movement.amount)}
                            </Text>
                          </Td>
                          <Td borderColor={borderColor} isNumeric>
                            {formatCurrency(movement.balanceAfter)}
                          </Td>
                          <Td borderColor={borderColor}>
                            <Badge colorScheme={getStatusColor(movement.status)}>
                              {getStatusLabel(movement.status)}
                            </Badge>
                          </Td>
                          <Td borderColor={borderColor}>
                            <Text fontSize="xs">
                              {movement.paymentMethodName || '-'}
                            </Text>
                          </Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
              </Box>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Modal de crear movimiento */}
      {isCreateOpen && (
        <CreateMovementModal
          isOpen={isCreateOpen}
          onClose={onCreateClose}
          account={account}
          onSuccess={handleMovementCreated}
        />
      )}
    </>
  );
}
